import React, { useMemo } from "react";
import { useSnackbar } from "notistack";
import { Box, Button, MenuItem, InputAdornment, Skeleton } from "@mui/material";
import { Formik, Field, Form } from "formik";
import { Select, TextField } from "formik-mui";

import { CancelButton } from "../components/Buttons";
import Sheet, {
  SheetContent,
  SheetHeader,
} from "../components/Sheet";
import LoadingSpinner from "../components/Loading";
import { useConfig } from "../hooks/useConfig";
import { freightTypeId } from "../freight/freightTypeId";
import { axios } from "../api";
import { handleAPIErrors } from "../utils/FormikErrors";
import { useCurrencies } from "../hooks/useCurrencies";
import type { Currency } from "../types";
import { InvoiceDetails, InvoiceLines } from "./types";

interface InvoiceAddFreightProps {
  onClose: () => void;
  open: boolean;
  invoice: InvoiceDetails;
  config: { [key: string]: any };
  globalConfig: { [key: string]: any };
}

interface AddFreightFormProps {
  onClose: () => void;
  invoice: InvoiceDetails;
  config: { [key: string]: any };
  globalConfig: { [key: string]: any };
  enqueueSnackbar: ReturnType<typeof useSnackbar>["enqueueSnackbar"];
}

const AddFreightForm: React.FC<AddFreightFormProps> = ({
  onClose,
  invoice,
  config,
  enqueueSnackbar,
}) => {
  const freightConfig = useConfig("freight");
  const currency = useCurrencies();

  const jobId = useMemo(() => {
    if (invoice?.lines && invoice.lines.length > 0) {
      const lineWithJobId = invoice.lines.find((line: InvoiceLines) => line.job_id);
      return lineWithJobId?.job_id || null;
    }
    return null;
  }, [invoice?.lines]);

  const division = useMemo(() => {
    return invoice?.division_id || (invoice as any)?.division || null;
  }, [invoice]);

  const initialValues = useMemo(
    () => ({
      title: "",
      cost_price: "0.0000",
      cost_currency: "USD",
      cost_exchange_rate: "1.0000",
      markup_percentage: "0.00",
      sell_price: "0.0000",
      sell_currency: "USD",
      sell_exchange_rate: "1.0000",
      description: "",
      job_id: jobId,
      status_id: 1,
    }),
    [jobId]
  );

  const handleSubmit = async (values: any, { setSubmitting, setErrors }) => {
    if (!jobId) {
      enqueueSnackbar("Cannot create freight: No job ID found in invoice", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
      setSubmitting(false);
      return;
    }

    const payload = {
      title: values.title,
      markup: Number(values.markup_percentage).toFixed(2),
      cost: Number(values.cost_price).toFixed(4),
      currency: values.cost_currency,
      exchange: Number(values.cost_exchange_rate).toFixed(4),
      sell_value: Number(values.sell_price).toFixed(4),
      sell: Number(values.sell_price).toFixed(4),
      sell_currency: values.sell_currency,
      sell_exchange: Number(values.sell_exchange_rate).toFixed(4),
      description: values.description,
      job_id: jobId,
      status: values?.status_id,
      freight_to: null,
      division: division,
      freight_to_name: "",
      freight_to_addrline1: "",
      freight_to_addrline2: "",
      freight_to_addrline3: "",
      freight_to_addrline4: "",
      type_id: freightTypeId(freightConfig, "type_misc"),
    };

    try {
      // First, create the freight item
      const freightResponse = await axios({
        method: "post",
        url: `/v6/freight`,
        data: payload,
      });

      const freightId = freightResponse.data?.id;

      // Build the line payload similar to InvoiceAddMisc, but type_id 11 for freight
      const line_payload = {
        item_id: freightId,
        quantity: 1,
        value: Number(values.sell_price),
        type_id: 11, // type_id 11 for freight
        line_description: values.title,
      };

      // Call invoice PUT API to attach freight line
      await axios({
        method: "put",
        url: `/v6/invoice/${invoice.id}`,
        data: {
          lines: [line_payload],
          status_id: invoice.status_id,
        },
        cache: {
          update: {
            [`get-invoice-${invoice.id}`]: "delete",
          },
        },
      });

      // Clear cache
      axios.storage.remove(`get-invoice-${invoice.id}`);
      axios.storage.remove(`get-lines-${invoice.id}`);

      setSubmitting(false);
      enqueueSnackbar(`Freight Added Successfully`, {
        variant: "success",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
      onClose();
    } catch (error) {
      setSubmitting(false);
      handleAPIErrors(error, setErrors);
      enqueueSnackbar("Error Adding Freight", {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
    }
  };

  const calculateMarkupPercentage = (costPrice: number, sellPrice: number) => {
    const cp = isNaN(costPrice) ? 0 : costPrice;
    const sp = isNaN(sellPrice) ? 0 : sellPrice;
    if (cp === 0) return 0;
    return ((sp - cp) / cp) * 100;
  };

  const calculateSellPrice = (costPrice: number, markupPercentage: number) => {
    const cp = isNaN(costPrice) ? 0 : costPrice;
    const mp = isNaN(markupPercentage) ? 0 : markupPercentage;
    return cp * (1 + mp / 100);
  };

  const formatPrice = (val: any) => {
    const n = Number(val);
    if (val === "" || isNaN(n)) return "";
    return n.toFixed(4);
  };

  const formatMarkup = (val: any) => {
    const n = Number(val);
    if (val === "" || isNaN(n)) return "";
    return n.toFixed(2);
  };

  if (!jobId) {
    return (
      <Box sx={{ p: 2 }}>
        <p>No job ID found in invoice. Cannot add freight items.</p>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ submitForm, isSubmitting, setFieldValue, values }) => (
        <Form>
          <>
            {/* Title */}
            <Box marginBottom={2}>
              <Field
                component={TextField}
                name="title"
                label="Title"
                fullWidth
              />
            </Box>

            {/* Cost Price, Currency, Exchange Rate */}
            <Box display="flex" gap={2} marginBottom={2}>
              <Field
                component={TextField}
                name="cost_price"
                label="Cost Price"
                type="text"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-input": { textAlign: "right" } }}
                inputProps={{
                  inputMode: "decimal",
                  pattern: "\\d*(\\.\\d{0,4})?",
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value === "") {
                    setFieldValue("cost_price", "");
                    return;
                  }
                  const cleaned = value.replace(/[^0-9.]/g, "");
                  const parts = cleaned.split(".");
                  let next = parts[0];
                  if (parts.length > 1) {
                    next += "." + parts[1].slice(0, 4);
                  }
                  setFieldValue("cost_price", next);
                }}
                onBlur={() => {
                  const newCostPrice = Number(values.cost_price);
                  const currentSellPrice = Number(values.sell_price);
                  const currentMarkupPercentage = Number(
                    values.markup_percentage
                  );

                  if (newCostPrice === 0) {
                    setFieldValue("sell_price", "0.0000");
                    setFieldValue("markup_percentage", "0.00");
                    setFieldValue(
                      "cost_price",
                      values.cost_price === ""
                        ? ""
                        : formatPrice(values.cost_price)
                    );
                    return;
                  }

                  if (currentSellPrice === 0 && currentMarkupPercentage === 0) {
                    setFieldValue(
                      "sell_price",
                      isNaN(newCostPrice) ? "" : Number(newCostPrice).toFixed(4)
                    );
                    setFieldValue("markup_percentage", "0.00");
                  } else if (
                    newCostPrice !== 0 &&
                    currentMarkupPercentage !== 0
                  ) {
                    const newSellPrice = calculateSellPrice(
                      newCostPrice,
                      currentMarkupPercentage
                    );
                    setFieldValue(
                      "sell_price",
                      isNaN(newSellPrice) ? "" : Number(newSellPrice).toFixed(4)
                    );
                  } else if (currentSellPrice !== 0 && newCostPrice !== 0) {
                    const newMarkup = calculateMarkupPercentage(
                      newCostPrice,
                      currentSellPrice
                    );
                    setFieldValue(
                      "markup_percentage",
                      isNaN(newMarkup) ? "" : Number(newMarkup).toFixed(2)
                    );
                  } else if (currentSellPrice === 0 && newCostPrice !== 0) {
                    setFieldValue("markup_percentage", "0.00");
                  }

                  setFieldValue(
                    "cost_price",
                    values.cost_price === ""
                      ? ""
                      : formatPrice(values.cost_price)
                  );
                }}
              />
              {currency ? (
                <Field
                  component={Select}
                  name="cost_currency"
                  label="Currency"
                  fullWidth
                  onChange={(e: any) =>
                    setFieldValue("cost_currency", e.target.value)
                  }
                  sx={{ ".MuiSelect-select": { textAlign: "right" } }}
                >
                  <MenuItem value="" key={0} disabled>
                    Currency
                  </MenuItem>
                  {currency.data.map((curr: Currency, index) => (
                    <MenuItem value={curr.currency_short} key={index + 1}>
                      {curr.currency_short}
                    </MenuItem>
                  ))}
                </Field>
              ) : (
                <Skeleton />
              )}
              <Field
                component={TextField}
                name="cost_exchange_rate"
                label="Exchange Rate"
                type="text"
                fullWidth
                inputProps={{
                  inputMode: "decimal",
                  pattern: "\\d*(\\.\\d{0,4})?",
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value === "") {
                    setFieldValue("cost_exchange_rate", "");
                    return;
                  }
                  const cleaned = value.replace(/[^0-9.]/g, "");
                  const parts = cleaned.split(".");
                  let next = parts[0];
                  if (parts.length > 1) {
                    next += "." + parts[1].slice(0, 4);
                  }
                  setFieldValue("cost_exchange_rate", next);
                }}
                onBlur={() => {
                  setFieldValue(
                    "cost_exchange_rate",
                    values.cost_exchange_rate === ""
                      ? ""
                      : formatPrice(values.cost_exchange_rate)
                  );
                }}
                sx={{ "& .MuiInputBase-input": { textAlign: "right" } }}
              />
            </Box>

            {/* Markup % */}
            <Box marginBottom={2}>
              <Field
                component={TextField}
                name="markup_percentage"
                label="Markup %"
                type="text"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-input": { textAlign: "right" } }}
                inputProps={{
                  inputMode: "decimal",
                  pattern: "\\d*(\\.\\d{0,2})?",
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value === "") {
                    setFieldValue("markup_percentage", "");
                    return;
                  }
                  const cleaned = value.replace(/[^0-9.]/g, "");
                  const parts = cleaned.split(".");
                  let next = parts[0];
                  if (parts.length > 1) {
                    next += "." + parts[1].slice(0, 2);
                  }
                  setFieldValue("markup_percentage", next);
                }}
                onBlur={() => {
                  const newMarkupPercentage = Number(values.markup_percentage);
                  const currentCostPrice = Number(values.cost_price);
                  if (currentCostPrice !== 0) {
                    const newSellPrice = calculateSellPrice(
                      currentCostPrice,
                      newMarkupPercentage
                    );
                    setFieldValue(
                      "sell_price",
                      isNaN(newSellPrice) ? "" : Number(newSellPrice).toFixed(4)
                    );
                  }

                  setFieldValue(
                    "markup_percentage",
                    values.markup_percentage === ""
                      ? ""
                      : formatMarkup(values.markup_percentage)
                  );
                }}
              />
            </Box>

            {/* Sell Price, Currency, Exchange Rate */}
            <Box display="flex" gap={2} marginBottom={2}>
              <Field
                component={TextField}
                name="sell_price"
                label="Sell Price"
                type="text"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-input": { textAlign: "right" } }}
                inputProps={{
                  inputMode: "decimal",
                  pattern: "\\d*(\\.\\d{0,4})?",
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value === "") {
                    setFieldValue("sell_price", "");
                    return;
                  }
                  const cleaned = value.replace(/[^0-9.]/g, "");
                  const parts = cleaned.split(".");
                  let next = parts[0];
                  if (parts.length > 1) {
                    next += "." + parts[1].slice(0, 4);
                  }
                  setFieldValue("sell_price", next);
                }}
                onBlur={() => {
                  const newSellPrice = Number(values.sell_price);
                  const currentCostPrice = Number(values.cost_price);
                  if (currentCostPrice !== 0) {
                    const newMarkup = calculateMarkupPercentage(
                      currentCostPrice,
                      newSellPrice
                    );
                    setFieldValue(
                      "markup_percentage",
                      isNaN(newMarkup) ? "" : Number(newMarkup).toFixed(2)
                    );
                  } else if (newSellPrice !== 0 && currentCostPrice === 0) {
                    setFieldValue("markup_percentage", "0.00");
                  }

                  setFieldValue(
                    "sell_price",
                    values.sell_price === ""
                      ? ""
                      : formatPrice(values.sell_price)
                  );
                }}
              />
              {currency ? (
                <Field
                  component={Select}
                  name="sell_currency"
                  label="Currency"
                  fullWidth
                  onChange={(e: any) =>
                    setFieldValue("sell_currency", e.target.value)
                  }
                  sx={{ ".MuiSelect-select": { textAlign: "right" } }}
                >
                  <MenuItem value="" key={0} disabled>
                    Currency
                  </MenuItem>
                  {currency.data.map((curr: Currency, index) => (
                    <MenuItem value={curr.currency_short} key={index + 1}>
                      {curr.currency_short}
                    </MenuItem>
                  ))}
                </Field>
              ) : (
                <Skeleton />
              )}
              <Field
                component={TextField}
                name="sell_exchange_rate"
                label="Exchange Rate"
                type="text"
                fullWidth
                inputProps={{
                  inputMode: "decimal",
                  pattern: "\\d*(\\.\\d{0,4})?",
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value === "") {
                    setFieldValue("sell_exchange_rate", "");
                    return;
                  }
                  const cleaned = value.replace(/[^0-9.]/g, "");
                  const parts = cleaned.split(".");
                  let next = parts[0];
                  if (parts.length > 1) {
                    next += "." + parts[1].slice(0, 4);
                  }
                  setFieldValue("sell_exchange_rate", next);
                }}
                onBlur={() => {
                  setFieldValue(
                    "sell_exchange_rate",
                    values.sell_exchange_rate === ""
                      ? ""
                      : Number(values.sell_exchange_rate).toFixed(4)
                  );
                }}
                sx={{ "& .MuiInputBase-input": { textAlign: "right" } }}
              />
            </Box>

            {/* Description */}
            <Box marginBottom={2}>
              <Field
                component={TextField}
                name="description"
                label="Description"
                multiline
                rows={4}
                fullWidth
              />
            </Box>

            {/* Status */}
            <Box marginBottom={2}>
              {freightConfig?.status ? (
                <Field
                  component={Select}
                  id="status"
                  name="status_id"
                  label="Status"
                  fullWidth
                >
                  {Object.entries(freightConfig.status).map(([key, value]) => (
                    <MenuItem value={key} key={key}>
                      {value as unknown as string}
                    </MenuItem>
                  ))}
                </Field>
              ) : (
                <></>
              )}
            </Box>
          </>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <CancelButton onClick={onClose} />
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              onClick={submitForm}
            >
              Submit
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

const InvoiceAddFreight: React.FC<InvoiceAddFreightProps> = (props) => {
  const { open, onClose, invoice, config, globalConfig } = props;
  const { enqueueSnackbar } = useSnackbar();
  const title = `Add Freight`;

  if (!config || !invoice) {
    return <LoadingSpinner />;
  }

  return (
    <Sheet open={open} onClose={onClose}>
      <SheetHeader onClose={onClose} title={title} />
      <SheetContent>
        <AddFreightForm
          onClose={onClose}
          invoice={invoice}
          config={config}
          globalConfig={globalConfig}
          enqueueSnackbar={enqueueSnackbar}
        />
      </SheetContent>
    </Sheet>
  );
};

export default InvoiceAddFreight;
